import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../../../../sass/components/forms.scss';
import '../../../../sass/components/profile_form_modal.scss';
import '../../../../sass/components/messaging_profile_form.scss';
import Gender from './components/Gender';
import AdvertiserRole from './components/AdvertiserRole';
import Occupation from './components/Occupation';
import DateOfBirth from './components/DateOfBirth';
import LengthOfStay from './components/LengthOfStay';
import MovingDateInput from './components/MovingDateInput';
import ProfilePhotoUpload from './components/ProfilePhotoUpload';
import { validateAgeAndDate, dateIsInPast } from './helpers/date_helpers';
import { formatData, formatPayload } from './helpers/format_data';
import useApi from './helpers/useApi';
import {
  trackBasicProfileShow,
  trackBasicProfileSubmit,
  trackBasicProfileSkip,
} from '../../tracking/trackingBasicProfile';

const formFields = ['gender', 'date_of_birth', 'occupation', 'date_available'];

// Either max or min term chosen to be considered complete
const completeFields = (data) =>
  formFields.every(
    (field) =>
      (data.user[field] || data.user.term_preferences[field]) &&
      (data.user.term_preferences?.min_term ||
        data.user.term_preferences?.max_term),
  );

const fieldsToShow = (data) => {
  const onlyPhotoMissing = completeFields(data) && !data.user.profile_photo;
  const allFieldsComplete = completeFields(data) && data.user.profile_photo;
  return { hideForm: allFieldsComplete, onlyPhotoMissing };
};

const MessagingProfileForm = ({
  uuid,
  subDir,
  country,
  isMobile,
  uploadSession,
  seeker,
  offerer,
  localisations,
}) => {
  const { apiError, data } = useApi('get', `/api/1.0/users/${uuid}/profiles`);
  const [intitialData, setIntitialData] = useState({});
  const [profileData, setProfileData] = useState({});
  const [errors, setErrors] = useState({ movingDate: '', dob: '', submit: '' });
  const [visibility, setVisibility] = useState({ hideForm: true });
  const [role, setRole] = useState('');

  const close = () => setVisibility({ hideForm: true });

  const skip = () => {
    trackBasicProfileSkip();
    close();
  };

  useEffect(() => {
    if (data) {
      setVisibility(fieldsToShow(data));
      const formattedData = formatData(data.user);
      trackBasicProfileShow();
      setProfileData(formattedData);
      setIntitialData(formattedData);
      setRole(formattedData.advertiser_role);
    }
  }, [data, offerer, seeker]);

  useEffect(() => {
    const wantedAdMessageBox = document.querySelector('.wanted-message-box');
    if (!visibility.hideForm && wantedAdMessageBox) {
      wantedAdMessageBox.style.display = 'none';
    }
    if (visibility.hideForm && wantedAdMessageBox) {
      wantedAdMessageBox.style.display = 'block';
    }
  }, [visibility]);

  const updateProfile = (name, value, parentObj) => {
    if (parentObj) {
      return setProfileData({
        ...profileData,
        [parentObj]: {
          ...profileData[parentObj],
          [name]: value,
        },
      });
    }
    return setProfileData({ ...profileData, [name]: value });
  };

  const dobUpdate = (date) => {
    setErrors({ ...errors, dob: '' });
    const { error } = validateAgeAndDate(date);

    if (error) {
      return setErrors({ ...errors, dob: error });
    }

    return updateProfile('split_date_of_birth', date);
  };

  // Used if date input falls back to text field
  const movingDateUpdate = (date) => {
    setErrors({ ...errors, movingDate: '' });

    const validFormat = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(date);
    const dateInPast = dateIsInPast(date);

    // if entered a value and not valid
    if ((!validFormat || dateInPast) && !!date) {
      setErrors({
        ...errors,
        movingDate: dateInPast
          ? "Please enter today's date or a future one"
          : 'Please use date format dd-mm-yyyy',
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (errors.movingDate || errors.dob) {
      return false;
    }

    // Photo upload is a different api so don't need to submit to profiles api
    if (visibility.onlyPhotoMissing) {
      return close();
    }

    const payload = formatPayload(profileData);

    return axios
      .patch(`/api/1.0/users/${uuid}/profiles`, payload)
      .then((response) => {
        trackBasicProfileSubmit(intitialData, profileData);
        setIntitialData(profileData);
        close();
      })
      .catch((e) => {
        console.log(e.response);
        setErrors({ ...errors, submit: e.message });
      });
  };

  return (
    <>
      {!visibility.hideForm && (
        <>
          <div className="profile-form__message-box">
            <div className="message-box message-box--info">
              <span className="message-box__icon">
                <i className="far fa-lightbulb-on"></i>
              </span>
              <p className="message-box__text">
                TOP TIP! Complete your profile for better responses
              </p>
            </div>
          </div>
          <form
            onSubmit={handleSubmit}
            className={`profile-form ${
              country === 'US' ? 'profile-form--us' : ''
            }`}
          >
            {data && !data.user.advertiser_role && offerer ? (
              <>
                <h2 className="profile-form__title">
                  About your place for rent
                </h2>
                <AdvertiserRole
                  updateProfile={updateProfile}
                  profileData={profileData}
                  setRole={setRole}
                  localisations={localisations}
                />
              </>
            ) : null}
            {seeker &&
            (!data.user.term_preferences?.date_available ||
              (!data.user.term_preferences?.min_term &&
                !data.user.term_preferences?.max_term)) ? (
              <h2 className="profile-form__title">
                About your search for a place
              </h2>
            ) : null}
            {data && !data.user.term_preferences?.date_available && seeker ? (
              <MovingDateInput
                profileData={profileData.term_preferences}
                movingDateUpdate={movingDateUpdate}
                movingDateError={errors.movingDate}
                updateProfile={updateProfile}
              />
            ) : null}
            {data &&
            !data.user.term_preferences?.min_term &&
            !data.user.term_preferences?.max_term &&
            seeker ? (
              <LengthOfStay
                updateProfile={updateProfile}
                profileData={profileData.term_preferences}
              />
            ) : null}
            {!data.user.profile_photo?.url ||
            !data.user.gender ||
            !data.user.date_of_birth ||
            (!data.user.occupation &&
              role !== 'live out landlord' &&
              role !== 'agent') ? (
              <h2 className="profile-form__title">About you</h2>
            ) : null}
            {data && !data.user.profile_photo?.url ? (
              <ProfilePhotoUpload
                profileData={profileData}
                subDir={subDir}
                uploadSession={uploadSession}
              />
            ) : null}
            {data && !data.user.gender ? (
              <Gender
                updateProfile={updateProfile}
                profileData={profileData}
                isMobile={isMobile}
              />
            ) : null}
            {data && !data.user.date_of_birth ? (
              <DateOfBirth
                dobUpdate={dobUpdate}
                dobError={errors.dob}
                profileData={profileData}
              />
            ) : null}
            {data &&
            !data.user.occupation &&
            role !== 'live out landlord' &&
            role !== 'agent' ? (
              <Occupation
                updateProfile={updateProfile}
                profileData={profileData}
              />
            ) : null}
            <div className="profile-form__buttons">
              <button
                type="button"
                className="button button--link"
                onClick={skip}
              >
                Skip
              </button>
              <div className="profile-form__submit">
                <button className="button">Save changes</button>
              </div>
            </div>
            <p
              className={`profile-form__error ${
                errors.submit || apiError ? 'profile-form__error--visible' : ''
              }`}
              aria-live="assertive"
            >
              {errors.submit || apiError}
            </p>
          </form>
        </>
      )}
    </>
  );
};

export default MessagingProfileForm;
